import React, { useState } from 'react';
import { Card, Table, Tag, Progress, Tabs, Spin, Alert, Empty } from 'antd';
import { TeamOutlined, InboxOutlined, CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { useResourceTracking } from '../../../hooks/useManufacturingDashboard';

interface ResourceTrackingPanelProps {
  refreshInterval?: number;
}

const ResourceTrackingPanel: React.FC<ResourceTrackingPanelProps> = ({ refreshInterval }) => {
  const [activeTab, setActiveTab] = useState('operators');
  const { data: resources, isLoading, error } = useResourceTracking(refreshInterval);

  if (error) {
    return (
      <Card title="Resource Tracking">
        <Alert
          message="Error Loading Resource Data"
          description={error.message}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card title="Resource Tracking">
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!resources || !resources.operatorList || !resources.materialConsumption) {
    return (
      <Card title="Resource Tracking">
        <Empty description="No resource data available" />
      </Card>
    );
  }

  const operatorColumns = [
    {
      title: 'Operator',
      dataIndex: 'operatorName',
      key: 'operatorName',
      render: (name: string, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.currentOperation && (
            <Tag color="processing" icon={<SyncOutlined spin />} style={{ marginTop: 4 }}>
              Active
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Current Operation',
      dataIndex: 'currentOperation',
      key: 'currentOperation',
      render: (operation?: string) => operation || <Tag color="default">Idle</Tag>,
    },
    {
      title: 'Workstation',
      dataIndex: 'workstation',
      key: 'workstation',
      render: (workstation?: string) => workstation || '-',
    },
    {
      title: 'Efficiency',
      dataIndex: 'efficiency',
      key: 'efficiency',
      width: 120,
      render: (efficiency: number) => (
        <div>
          <div style={{ 
            fontSize: 14, 
            fontWeight: 500,
            color: efficiency >= 90 ? '#52c41a' : efficiency >= 75 ? '#faad14' : '#ff4d4f'
          }}>
            {efficiency.toFixed(1)}%
          </div>
          <Progress
            percent={efficiency}
            size="small"
            strokeColor={
              efficiency >= 90 ? '#52c41a' : efficiency >= 75 ? '#faad14' : '#ff4d4f'
            }
            showInfo={false}
          />
        </div>
      ),
      sorter: (a: any, b: any) => a.efficiency - b.efficiency,
    },
    {
      title: 'Hours Worked',
      dataIndex: 'hoursWorked',
      key: 'hoursWorked',
      width: 100,
      render: (hours: number) => `${hours.toFixed(1)}h`,
      sorter: (a: any, b: any) => a.hoursWorked - b.hoursWorked,
    },
  ];

  const materialColumns = [
    {
      title: 'Material',
      dataIndex: 'itemName',
      key: 'itemName',
    },
    {
      title: 'Consumed',
      dataIndex: 'consumed',
      key: 'consumed',
      width: 100,
      render: (consumed: number) => consumed.toLocaleString(),
    },
    {
      title: 'Planned',
      dataIndex: 'planned',
      key: 'planned',
      width: 100,
      render: (planned: number) => planned.toLocaleString(),
    },
    {
      title: 'Variance',
      dataIndex: 'variance',
      key: 'variance',
      width: 120,
      render: (variance: number, record: any) => {
        const percentage = record.planned > 0 ? (variance / record.planned) * 100 : 0;
        const isNegative = variance < 0;
        return (
          <div>
            <Tag color={isNegative ? 'success' : variance > 0 ? 'error' : 'default'}>
              {isNegative ? '' : '+'}{variance}
            </Tag>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
              {isNegative ? '' : '+'}{percentage.toFixed(1)}%
            </div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (_: any, record: any) => {
        const percentage = record.planned > 0 ? (record.consumed / record.planned) * 100 : 0;
        return (
          <Progress
            percent={percentage}
            strokeColor={percentage > 100 ? '#ff4d4f' : percentage > 95 ? '#faad14' : '#52c41a'}
            format={(percent) => `${percent?.toFixed(0)}%`}
            size="small"
          />
        );
      },
    },
  ];

  const tabItems = [
    {
      key: 'operators',
      label: (
        <span>
          <TeamOutlined /> Operators ({resources.operatorList.length})
        </span>
      ),
      children: (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
            <Card size="small" style={{ flex: 1 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 500, color: '#52c41a' }}>
                  <CheckCircleOutlined /> {resources.activeOperators}
                </div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Active Operators</div>
              </div>
            </Card>
            <Card size="small" style={{ flex: 1 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 500, color: '#145A54' }}>
                  <TeamOutlined /> {resources.totalOperators}
                </div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Total Operators</div>
              </div>
            </Card>
            <Card size="small" style={{ flex: 1 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 500, color: '#722ed1' }}>
                  {resources.totalOperators > 0 
                    ? ((resources.activeOperators / resources.totalOperators) * 100).toFixed(0)
                    : 0}%
                </div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Utilization</div>
              </div>
            </Card>
          </div>
          <Table
            dataSource={resources.operatorList}
            columns={operatorColumns}
            rowKey="operatorId"
            size="small"
            pagination={{ pageSize: 10 }}
          />
        </div>
      ),
    },
    {
      key: 'materials',
      label: (
        <span>
          <InboxOutlined /> Materials ({resources.materialConsumption.length})
        </span>
      ),
      children: resources.materialConsumption.length > 0 ? (
        <Table
          dataSource={resources.materialConsumption}
          columns={materialColumns}
          rowKey="itemName"
          size="small"
          pagination={{ pageSize: 10 }}
        />
      ) : (
        <Empty description="No material consumption data available" />
      ),
    },
  ];

  return (
    <Card title="Resource Tracking">
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
    </Card>
  );
};

export default ResourceTrackingPanel;
